import { faArrowDownToLine, faEye, faTrash } from '@fortawesome/pro-regular-svg-icons'
import { GridColDef } from '@mui/x-data-grid'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HicDataGridPremium } from 'src/app/components/hic-vapor/datagrid/datagrid'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import AttacheViewer from './attache-viewer'
import { FileState, isImage, FileTypeAccepted, isPDF, isEmail, isHTML } from './attaches.type'
import { Action } from 'src/app/components/data-grid-action-cell-item'

export default function AttachesGrid({ loading, rows, onDeleteClick }: { loading: boolean; rows: FileState[]; onDeleteClick: (fileId: string) => void }) {
  const [fileToShow, setFileToShow] = useState<FileState | null>(null)
  const columns = getColumns({ onViewAttache: (file) => setFileToShow(file), onDeleteClick })

  return (
    <>
      <HicDataGridPremium sx={{ maxWidth: '800px' }} loading={loading} columns={columns} autoHeight isRowSelectable={() => false} hideFooter={true} disableColumnMenu rows={rows} />
      <AttacheViewer
        file={fileToShow}
        open={Boolean(fileToShow)}
        setClose={() => {
          setFileToShow(null)
        }}
      />
    </>
  )
}

function getColumns({ onViewAttache, onDeleteClick }: { onViewAttache: (file: FileState) => void; onDeleteClick: (fileId: string) => void }) {
  const { t } = useTranslation()
  const formatDate = useFormatDate()

  function getFileTypeTranslated(type: FileTypeAccepted) {
    if (isImage(type)) {
      return t('pms.booking.reservation.tab.attaches.type.image')
    } else if (isPDF(type)) {
      return t('pms.booking.reservation.tab.attaches.type.document')
    } else if (isEmail(type)) {
      return t('pms.booking.reservation.tab.attaches.type.email')
    } else if (isHTML(type)) {
      return t('pms.booking.reservation.tab.attaches.type.html')
    } else {
      return t('pms.booking.reservation.tab.attaches.type.other')
    }
  }

  const columns: GridColDef[] = [
    {
      field: 'createOn',
      headerName: t('pms.booking.reservation.tab.attaches.header.date'),
      width: 175,
      sortable: true,
      valueFormatter: ({ value }) => formatDate(value as Date, 'Ppp'),
    },
    {
      field: 'name',
      headerName: t('pms.booking.reservation.tab.attaches.header.name'),
      sortable: true,
      flex: 1,
    },
    {
      field: 'type',
      headerName: t('pms.booking.reservation.tab.attaches.header.type'),
      width: 150,
      sortable: true,
      valueFormatter: ({ value }) => getFileTypeTranslated(value as FileTypeAccepted),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('commons.table_actions'),
      getActions: (params) => [
        isPDF(params.row.type) || isImage(params.row.type) ? (
          <Action
            icon={faEye}
            label="View"
            onClick={() => {
              onViewAttache(params.row)
            }}
          />
        ) : (
          <></>
        ),
        <Action
          icon={faArrowDownToLine}
          label="Download"
          onClick={async () => {
            const file = params.row as FileState

            const link = document.createElement('a')
            link.download = file.name
            link.href = file.URL
            link.type = file.type
            link.target = '_blank'
            link.rel = 'noopener noreferrer'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          }}
        />,
        <Action
          icon={faTrash}
          label="Delete"
          onClick={() => {
            onDeleteClick(params.row.id)
          }}
        />,
      ],
    },
  ]

  return columns
}
